import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  useScrollTrigger,
  useMediaQuery,
  useTheme,
  IconButton,
  InputBase,
  Paper,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon, Search } from '@mui/icons-material';
import { navItems } from '../../content/nav';
import GlowButton from '../UI/GlowButton';

const Navbar = memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  }, [searchQuery, navigate]);

  const toggleMobileMenu = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const toggleSearch = useCallback(() => {
    setSearchOpen((prev) => !prev);
  }, []);

  const isActive = useCallback((path) => {
    return location.pathname === path;
  }, [location.pathname]);

  const navbarBackground = useMemo(() => {
    return trigger
      ? 'rgba(11, 15, 26, 0.9)'
      : 'rgba(11, 15, 26, 0.7)';
  }, [trigger]);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'transparent',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          boxShadow: 'none',
          zIndex: 1100,
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          pt: trigger ? { xs: 1, md: 1.5 } : { xs: 2, md: 3 },
          pb: trigger ? { xs: 0.5, md: 1 } : { xs: 1, md: 2 },
          transition: 'padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'sticky',
          display: 'block',
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: '95%', sm: '90%', md: '1200px', lg: '1400px' },
            mx: 'auto',
            px: { xs: 1, sm: 2 },
          }}
        >
          <Toolbar 
            sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: trigger ? { xs: 0.75, md: 1 } : { xs: 1, md: 1.25 },
              px: { xs: 2, sm: 3, md: 4 },
              background: navbarBackground,
              backdropFilter: isMobile ? 'blur(15px)' : 'blur(25px)',
              WebkitBackdropFilter: isMobile ? 'blur(15px)' : 'blur(25px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: trigger ? { xs: '16px', md: '20px' } : { xs: '20px', md: '30px' },
              boxShadow: trigger 
                ? '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(168, 85, 247, 0.2)' 
                : '0 4px 24px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              willChange: 'background, box-shadow, border-color, border-radius, padding',
              position: 'relative',
              overflow: 'hidden',
              minHeight: trigger ? { xs: '52px', md: '60px' } : { xs: '56px', md: '64px' },
              zIndex: 1,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                padding: '1px',
                background: trigger
                  ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.3) 0%, rgba(236, 72, 153, 0.2) 100%)'
                  : 'linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(236, 72, 153, 0.05) 100%)',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                pointerEvents: 'none',
                transition: 'opacity 0.3s ease',
                opacity: trigger ? 1 : 0.5,
              },
              '&:hover': {
                borderColor: 'rgba(168, 85, 247, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(168, 85, 247, 0.15)',
              },
            }}
          >
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.75, md: 1 },
              transition: 'transform 0.2s ease',
              flexShrink: 0,
              zIndex: 10,
              position: 'relative',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="Swytrix Technologies"
              onError={(e) => {
                console.error('Logo image failed to load');
                e.target.style.display = 'none';
              }}
              sx={{
                width: { xs: 45, sm: 50, md: 55, lg: 60 },
                height: { xs: 45, sm: 50, md: 55, lg: 60 },
                objectFit: 'contain',
                filter: 'drop-shadow(0 2px 8px rgba(168, 85, 247, 0.4)) brightness(1.1)',
                transition: 'all 0.3s ease',
                display: 'block',
                flexShrink: 0,
                '&:hover': {
                  filter: 'drop-shadow(0 4px 16px rgba(168, 85, 247, 0.6)) brightness(1.2)',
                  transform: 'scale(1.05)',
                },
              }}
            />
            <Box 
              component="span" 
              sx={{ 
                display: { xs: 'none', sm: 'block' },
                fontSize: { sm: '1.1rem', md: '1.25rem', lg: '1.4rem' },
                fontWeight: 700,
                background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                whiteSpace: 'nowrap',
              }}
            >
              Swytrix Technologies
            </Box>
          </Box>

          {/* Desktop Navigation - Centered */}
          {!isMobile && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { md: 0.5, lg: 1 },
                justifyContent: 'center',
                flex: 1,
                mx: { md: 2, lg: 4 },
              }}
            >
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    sx={{
                      color: active ? '#fff' : 'rgba(255, 255, 255, 0.75)',
                      textTransform: 'none',
                      fontWeight: active ? 600 : 500,
                      position: 'relative',
                      fontSize: { md: '0.875rem', lg: '0.9rem' },
                      px: { md: 1.75, lg: 2.25 },
                      py: { md: 0.75, lg: 0.875 },
                      borderRadius: '12px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: active 
                        ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(236, 72, 153, 0.15) 100%)'
                        : 'transparent',
                      border: active 
                        ? '1px solid rgba(168, 85, 247, 0.3)'
                        : '1px solid transparent',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        zIndex: -1,
                      },
                      '&:hover': {
                        color: '#fff',
                        background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.25) 0%, rgba(236, 72, 153, 0.2) 100%)',
                        borderColor: 'rgba(168, 85, 247, 0.4)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(168, 85, 247, 0.2)',
                        '&::before': {
                          opacity: 0.1,
                        },
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>
          )}

          {/* Right Side Actions */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.5, sm: 0.75 },
            flexShrink: 0,
          }}>
            <IconButton
              onClick={toggleSearch}
              sx={{
                color: 'rgba(255, 255, 255, 0.75)',
                p: { xs: 0.75, sm: 0.875 },
                borderRadius: '10px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid transparent',
                '&:hover': { 
                  color: '#fff', 
                  background: 'rgba(168, 85, 247, 0.15)',
                  borderColor: 'rgba(168, 85, 247, 0.3)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(168, 85, 247, 0.2)',
                },
              }}
              aria-label="Search"
            >
              <Search sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }} />
            </IconButton>

            {!isMobile && (
              <GlowButton
                component={Link}
                to="/contact"
                size="small"
                sx={{ 
                  ml: { md: 0.5, lg: 0.75 },
                  px: { md: 2, lg: 2.5 },
                  fontSize: { md: '0.8rem', lg: '0.875rem' },
                  borderRadius: '12px',
                  height: { md: '36px', lg: '38px' },
                }}
              >
                Get Started
              </GlowButton>
            )}

            {isMobile && (
              <IconButton
                onClick={toggleMobileMenu}
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.75)', 
                  ml: 0.5,
                  p: 0.875,
                  borderRadius: '10px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: '1px solid transparent',
                  '&:hover': {
                    background: 'rgba(168, 85, 247, 0.15)',
                    borderColor: 'rgba(168, 85, 247, 0.3)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(168, 85, 247, 0.2)',
                  },
                }}
                aria-label="Menu"
              >
                {mobileOpen ? (
                  <CloseIcon sx={{ fontSize: '1.4rem' }} />
                ) : (
                  <MenuIcon sx={{ fontSize: '1.4rem' }} />
                )}
              </IconButton>
            )}
          </Box>
        </Toolbar>
        </Box>

        {/* Search Bar - CSS Transition */}
        <Collapse in={searchOpen} timeout={300}>
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              maxWidth: { xs: '95%', sm: '90%', md: '1200px', lg: '1400px' },
              mx: 'auto',
              px: { xs: 2, md: 4 },
              pb: 2,
              pt: 1,
            }}
          >
            <Box
              sx={{
                background: 'rgba(11, 15, 26, 0.75)',
                backdropFilter: 'blur(25px)',
                WebkitBackdropFilter: 'blur(25px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                px: 2,
                py: 1.5,
              }}
            >
            <Paper
              sx={{
                p: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                transition: 'border-color 0.2s ease',
                '&:focus-within': {
                  borderColor: '#a855f7',
                },
              }}
            >
              <InputBase
                placeholder="Search services, case studies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  ml: 1,
                  flex: 1,
                  color: 'white',
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  '&::placeholder': { 
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1,
                  },
                }}
                autoFocus
              />
              <IconButton 
                type="submit" 
                sx={{ 
                  p: '8px', 
                  color: '#a855f7',
                  '&:hover': {
                    background: 'rgba(168, 85, 247, 0.1)',
                  },
                }}
              >
                <Search />
              </IconButton>
            </Paper>
            </Box>
          </Box>
        </Collapse>

        {/* Mobile Menu - CSS Transition */}
        {isMobile && (
          <Collapse in={mobileOpen} timeout={300}>
            <Box
              sx={{
                maxWidth: { xs: '95%', sm: '90%', md: '1200px', lg: '1400px' },
                mx: 'auto',
                px: { xs: 1, sm: 2 },
                pt: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'rgba(11, 15, 26, 0.85)',
                  backdropFilter: 'blur(25px)',
                  WebkitBackdropFilter: 'blur(25px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  overflow: 'hidden',
                }}
              >
              {/* Mobile Menu Logo */}
              <Box
                component={Link}
                to="/"
                onClick={() => setMobileOpen(false)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 2,
                  px: 3,
                  textDecoration: 'none',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="Swytrix Technologies"
                  onError={(e) => {
                    console.error('Logo image failed to load');
                    e.target.style.display = 'none';
                  }}
                  sx={{
                    width: 50,
                    height: 50,
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 2px 8px rgba(168, 85, 247, 0.4)) brightness(1.1)',
                    display: 'block',
                    flexShrink: 0,
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Swytrix Technologies
                </Box>
              </Box>
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    fullWidth
                    onClick={() => setMobileOpen(false)}
                    sx={{
                      color: active ? '#a855f7' : 'rgba(255, 255, 255, 0.9)',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      fontWeight: active ? 600 : 500,
                      py: 2,
                      px: 3,
                      fontSize: '1rem',
                      borderLeft: active ? '3px solid #a855f7' : '3px solid transparent',
                      background: active ? 'rgba(168, 85, 247, 0.05)' : 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(168, 85, 247, 0.1)',
                        color: '#a855f7',
                        paddingLeft: '1.5rem',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
              <Box 
                sx={{ 
                  mt: 2, 
                  pt: 2, 
                  pb: 3,
                  px: 3,
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)' 
                }}
              >
                <GlowButton
                  component={Link}
                  to="/contact"
                  fullWidth
                  size="large"
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                  }}
                >
                  Get Started
                </GlowButton>
              </Box>
              </Box>
            </Box>
          </Collapse>
        )}
      </AppBar>
    </>
  );
});

Navbar.displayName = 'Navbar';

export default Navbar;

